import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { MessageSquare, Send, CheckCircle, User, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import ValidatedInput from './ValidatedInput';
import ValidatedSelect from './ValidatedSelect';
import ValidatedTextarea from './ValidatedTextarea';
import { validationRules } from '../utils/validation';
import './ValidatedInput.css';
import './ContactFormBox.css';

/**
 * Reusable contact form - same style as Help & Support.
 * Sends to /api/help/contact, emails go to tnputnam@live.com.
 * @param {string} title - Section title (e.g. "Contact Us", "Legal Questions")
 * @param {string} subtitle - Description text
 * @param {string} [fixedSubject] - If set, use this subject and hide subject dropdown
 * @param {Array<{value:string,label:string}>} [subjectOptions] - Options for subject dropdown (required if no fixedSubject)
 */
const ContactFormBox = ({ title, subtitle, fixedSubject, subjectOptions = [] }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitSuccess(false);

    const payload = fixedSubject
      ? { ...data, subject: fixedSubject }
      : data;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post('/api/help/contact', payload, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.data.success) {
        setSubmitSuccess(true);
        reset();
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        toast.error(response.data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-form-box">
      <h2 className="contact-form-box-title">
        <MessageSquare className="contact-form-box-icon" />
        {title}
      </h2>
      <p className="contact-form-box-subtitle">{subtitle}</p>

      {submitSuccess && (
        <div className="contact-form-box-success">
          <CheckCircle className="contact-form-box-success-icon" />
          <div>
            <strong>Message Sent!</strong>
            <p>We've received your message and will respond to {localStorage.getItem('userEmail') || 'your email'} within 24 hours.</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="contact-form-box-form">
        <ValidatedInput
          name="name"
          label="Your Name"
          type="text"
          register={register}
          errors={errors}
          placeholder="Enter your name"
          icon={User}
          required
          {...register('name', {
            ...validationRules.name,
            required: 'Name is required'
          })}
        />

        <ValidatedInput
          name="email"
          label="Your Email"
          type="email"
          register={register}
          errors={errors}
          placeholder="your.email@example.com"
          icon={Mail}
          required
          {...register('email', validationRules.email)}
        />

        {!fixedSubject && subjectOptions.length > 0 && (
          <ValidatedSelect
            name="subject"
            label="Subject"
            register={register}
            errors={errors}
            placeholder="Select a topic..."
            required
            options={subjectOptions}
            {...register('subject', {
              required: 'Subject is required'
            })}
          />
        )}

        <ValidatedTextarea
          name="message"
          label="Message"
          register={register}
          errors={errors}
          placeholder="Tell us how we can help you..."
          required
          rows={6}
          {...register('message', {
            required: 'Message is required',
            minLength: {
              value: 10,
              message: 'Message must be at least 10 characters'
            }
          })}
        />

        <button
          type="submit"
          disabled={isSubmitting}
          className="contact-form-box-submit"
        >
          {isSubmitting ? (
            <>
              <div className="contact-form-box-spinner"></div>
              Sending...
            </>
          ) : (
            <>
              <Send className="contact-form-box-button-icon" />
              Send Message
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactFormBox;
